import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Alert, Typography, Backdrop, CircularProgress, Link, Box } from "@mui/material";
import BodyContainer from "../components/BodyContainer";
import AppHeader from "../components/AppHeader";
import FooterButtons from "../components/FooterButtons";
import { appRoutes } from "../AppRoutes";
import { useNavigate } from "react-router-dom";
import { ReviewDisplay } from "../components/ReviewDisplay";

import { submitBurnInfo } from "../services/burns.service";
import { store } from "../store";
import { setRecommendationDenial } from "../store/submission.slice";
import { setBurnDataStale } from "../store/cache.slice";

const Review = () => {
	const isOnline = useSelector(state => state.cache.isOnline);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [error, setError] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const [tonsDenied, setTonsDenied] = useState(false);
	const [weatherDenied, setWeatherDenied] = useState(false);

	const totalAllowedAcres = useSelector(state => state.submission.recommendation?.totalAllowedAcres);

	useEffect(() => {
		const data = store.getState().submission;
		checkRecommendation(data);
	}, [])

	const submitInfo = async () => {
		const data = store.getState().submission;
		setSubmitting(true);
		try {
			await submitBurnInfo({ ...data });
			dispatch(setBurnDataStale());
			setSubmitting(false);
			navigate(appRoutes.SubmitSuccess);
		} catch(e) {
			setSubmitting(false);
			setError('Could not submit your burn. Please try again later.')
		}
	}

	const checkRecommendation = (data) => {
		const {weather, recommendation} = data;

		// If bad weather
		if ((weather.windSpeed && !weather.windSpeed.good) || (weather.humidity && !weather.humidity.good)) {
			setWeatherDenied(true);
			dispatch(setRecommendationDenial({badWeather: true}));
		}

		// If tons exceeded
		if (recommendation.allocatedTonnage <  recommendation.totalTonnageForAllBurns) {
			setTonsDenied(true);
			dispatch(setRecommendationDenial({tonsExceeded: true}));
		}
	}

	return (
		<>
			<AppHeader />
			<Backdrop open={submitting}>
				<CircularProgress color="inherit"/>
			</Backdrop>
			<BodyContainer hasFooter={false} hasHeader={true}>
				<Grid item xs="auto" mb={4} mt={2} sx={{ width: '100% !important' }}>
					<Typography variant="title" component="h1">Review your burn</Typography>
					<Typography variant="subtitle2" component="h2">
						Now that your Smoke Management Checklist is complete, please review your burn to ensure that the information is correct and submit the burn to the Arkansas Department of Agriculture Dispatch Center.
						Your submission is important to others planning burns in the same airshed to ensure that they are able to follow the voluntary smoke management guidelines.
					</Typography>

					{!isOnline && 
						<Alert severity="warning" sx={{marginBottom: '6px', marginTop: '6px'}}>
							This information may be out of date. Please find an internet connection and complete the submission process.
						</Alert>
					}
				</Grid>
				<Grid item xs={8} mt={2} sx={{ width: '100% !important', maxWidth: '600px !important' }}>
					<ReviewDisplay />
				</Grid>
				<Grid item xs={8} mt={2} sx={{ width: '100% !important', maxWidth: '600px !important' }}>
					{weatherDenied && 
					<Alert severity="warning" sx={{ marginTop: '6px'}}>
						The weather conditions for this day <b>exceeds the recommended threshold</b> for either or both humidity level and wind speed.
						Check to be sure relative humidity is above 20% and that wind speed is less than 15 mph at the time of the burn.
						<br/>
						If conditions do not improve, it is recommended to delay the burn to a later date or contact the ADA Dispatch Center.
					</Alert>
					}

					{tonsDenied &&
					<Alert severity="warning" sx={{marginTop: '6px'}}>
						The total tonnage of this burn <b>exceeds the total airshed allotment available.</b>
						Please revise your burn plan to reduce the acres burned on this burn day. 
						The available allotment acreage for this burn on this burn day is provided below. 
						You may go back to the “Determine Total Available Fuel Loading” screen to reduce the acreage for the planned burn.
					</Alert>
					}
					{(tonsDenied && totalAllowedAcres!= null) &&
					<Typography variant="subtitle">
						<Box fontWeight="fontWeightMedium" textAlign="left" mt={3}>
							Available Allotment Acreage:
							<Box ml={2} sx={{display: 'inline-block', maxWidth: '15px'}}>
								{totalAllowedAcres.toFixed(1)}
							</Box>
						</Box>
					</Typography>
					}
				</Grid>
				<Grid item xs="auto" mt={4} sx={{width: '100% !important'}}>
					<Alert
						severity="error"
						sx={{ display: error ? 'flex' : 'none', position: 'fixed', zIndex: 4, bottom: 80, width: '90vw', left: 'calc(50% - 45vw)' }}
						onClose={() => setError(false)}>
							{error}
					</Alert>
					<FooterButtons backLabel="Go Back" onNext={submitInfo} nextDisabled={!isOnline} nextLabel="Submit" />
				</Grid>
			</BodyContainer>
		</>
	)
}

export default Review;